import { InstaQLResult } from '@instantdb/react';
import * as Yup from 'yup';
import { AppSchema } from '@zw-app/shared/instant.schema';
import convert from 'convert-units';
import { db } from './instant';

const sortByOrderField = { $: { order: { order: 'asc' } } } as const;
const emptyResult = { data: undefined, isLoading: false, error: undefined };

export const queryCurrentUser = () =>
    db.useQuery({
        $users: {
            settings: {},
        },
    });

export const queryEvents = (enabled: boolean = true) =>
    db.useQuery(
        enabled
            ? {
                  events: {
                      group: {},
                      // Only fetch basic event data for the list view
                      ...sortByOrderField,
                  },
                  eventGroups: {
                      events: {
                          ...sortByOrderField,
                      },
                      ...sortByOrderField,
                  },
              }
            : null
    );

/** Events list for logistics dialogs — no eventGroups, field-scoped. */
export const queryEventsList = (enabled: boolean = true) =>
    db.useQuery(
        enabled
            ? {
                  events: {
                      $: {
                          fields: ['id', 'name', 'startDate', 'endDate'],
                          order: { order: 'asc' },
                      },
                  },
              }
            : null
    );

type QueryEventsListResponse = NonNullable<ReturnType<typeof queryEventsList>['data']>;
export type EventListItem = QueryEventsListResponse['events'][number];

type QueryEventsResponse = NonNullable<ReturnType<typeof queryEvents>['data']>;
export type Event = QueryEventsResponse['events'][number];
export type EventGroup = QueryEventsResponse['eventGroups'][number];

type QueryCurrentUserResponse = NonNullable<ReturnType<typeof queryCurrentUser>['data']>;
export type User = QueryCurrentUserResponse['$users'][number];
export type UserSettings = NonNullable<User['settings']>;

const eventDetailsQuery = (eventId: string) => ({
    events: {
        $: {
            where: {
                id: eventId,
            },
        },
        maps: {
            pins: {
                stationType: {},
            },
            $: { order: { order: 'asc' as const } },
        },
        datasets: {
            $: { order: { order: 'asc' as const } },
        },
        customMaterials: {
            category: {},
            $: { order: { order: 'asc' as const } },
        },
        reports: {
            charts: {},
            template: {},
            materials: {},
            categories: {},
            datasets: {},
            $: { order: { createdAt: 'asc' as const } },
        },
    },
});

export const queryEventDetails = (eventId?: string | null, options?: { secret?: string }) =>
    eventId
        ? db.useQuery(eventDetailsQuery(eventId), options?.secret != null ? { ruleParams: { secret: options.secret } } : undefined)
        : emptyResult;

export const queryEventTodo = (eventId?: string | null, todoTypeId?: string, completed?: boolean, options?: { secret?: string }) =>
    eventId
        ? db.useQuery(
              {
                  events: {
                      $: {
                          where: {
                              id: eventId,
                          },
                      },
                      todos: {
                          todoType: {},
                          mapPin: {
                              stationType: {},
                              map: {},
                          },
                          $: {
                              where: {
                                  ...(completed !== undefined ? { completed: completed } : {}),
                                  ...(todoTypeId ? { 'todoType.id': todoTypeId } : {}),
                              },
                              order: { created_at: 'asc' },
                          },
                      },
                  },
              },
              options?.secret != null ? { ruleParams: { secret: options.secret } } : undefined
          )
        : emptyResult;

export const queryLinksForCustomMaterial = (customMaterialId?: string) =>
    customMaterialId
        ? db.useQuery({
              wasteMeasurements: {
                  $: {
                      where: {
                          'material.id': customMaterialId,
                      },
                  },
              },
              wasteCategories: {
                  $: {
                      where: {
                          id: customMaterialId,
                      },
                  },
              },
          })
        : emptyResult;

export const queryReportData = (reportId?: string) =>
    reportId
        ? db.useQuery({
              reports: {
                  $: {
                      where: {
                          id: reportId,
                      },
                  },
                  event: {},
                  template: {},
                  materials: {},
                  categories: {},
                  datasets: {},
              },
          })
        : emptyResult;

type QueryEventDetailsResponse = NonNullable<ReturnType<typeof queryEventDetails>['data']>;
export type EventDetails = NonNullable<QueryEventDetailsResponse['events']>[number];
export type EventMap = NonNullable<EventDetails['maps']>[number];
export type Report = NonNullable<EventDetails['reports']>[number];
export type WasteDataset = NonNullable<EventDetails['datasets']>[number];
export type MapPin = NonNullable<EventMap['pins']>[number];
export type StationType = NonNullable<MapPin['stationType']>;

type QueryEventTodoResponse = NonNullable<ReturnType<typeof queryEventTodo>['data']>;
export type EventTodo = NonNullable<QueryEventTodoResponse['events']>[number];
export type Todo = EventTodo['todos'][number];
export type TodoType = NonNullable<Todo['todoType']>;

const wasteMeasurementsQueryStructure = {
    wasteMeasurements: {
        $: {
            order: {
                timestamp: 'asc',
            },
        },
        material: {
            category: {},
            customUnits: {},
        },
        dataset: {},
    },
} as const;

export const queryWasteMeasurementsForDataset = (datasetId: string) =>
    db.useQuery({
        ...wasteMeasurementsQueryStructure,
        wasteMeasurements: {
            ...wasteMeasurementsQueryStructure.wasteMeasurements,
            $: {
                ...wasteMeasurementsQueryStructure.wasteMeasurements.$,
                where: {
                    'dataset.id': datasetId,
                },
            },
        },
    });

export const queryWasteMeasurementsForEvent = (eventId: string) =>
    db.useQuery({
        ...wasteMeasurementsQueryStructure,
        wasteMeasurements: {
            ...wasteMeasurementsQueryStructure.wasteMeasurements,
            $: {
                ...wasteMeasurementsQueryStructure.wasteMeasurements.$,
                where: {
                    'dataset.event.id': eventId,
                },
            },
        },
    });

export const queryOnceWasteMeasurementsForEvent = (eventId: string) =>
    db.queryOnce({
        ...wasteMeasurementsQueryStructure,
        wasteMeasurements: {
            ...wasteMeasurementsQueryStructure.wasteMeasurements,
            $: {
                ...wasteMeasurementsQueryStructure.wasteMeasurements.$,
                where: {
                    'dataset.event.id': eventId,
                },
            },
        },
    });

type QueryWasteMeasurementsResponse = NonNullable<ReturnType<typeof queryWasteMeasurementsForDataset>['data']>;
export type WasteMeasurement = QueryWasteMeasurementsResponse['wasteMeasurements'][number];
export type MeasurementUnit = WasteMeasurement['units'];

export const queryReportTemplates = () =>
    db.useQuery({
        reportTemplates: {
            chartTemplates: {},
        },
    });

// Add this query function for fetching a specific report template
export const queryOnceReportTemplate = (templateId: string) =>
    db.queryOnce({
        reportTemplates: {
            $: {
                where: {
                    id: templateId,
                },
            },
            chartTemplates: {},
        },
    });

type QueryReportTemplatesResponse = NonNullable<ReturnType<typeof queryReportTemplates>['data']>;
export type ReportTemplate = QueryReportTemplatesResponse['reportTemplates'][number];

const massUnits = convert().possibilities('mass');
const volumeUnits = convert().possibilities('volume');
/** Base allowed units (all mass + volume from convert-units); custom units are added per material */
export const baseValidUnits = [...massUnits, ...volumeUnits] as MeasurementUnit[];
export const dataInputValidationSchema = Yup.object({
    quantity: Yup.number().typeError('Quantity must be a valid number').required('Quantity is required'),
    units: Yup.string().oneOf(baseValidUnits, 'Invalid unit').required('Units are required'),
    categoryId: Yup.string().required('Category is required'),
    materialId: Yup.string().required('Category is required'),
});

/** Build validation schema with allowed units = base (lb, yd3, gal, etc.) + material custom unit names */
export function getDataInputValidationSchema(validUnitValues: string[]) {
    return Yup.object({
        quantity: Yup.number().typeError('Quantity must be a valid number').required('Quantity is required'),
        units: Yup.string().oneOf(validUnitValues, 'Invalid unit').required('Units are required'),
        categoryId: Yup.string().required('Category is required'),
        materialId: Yup.string().required('Category is required'),
    });
}

export type DataInputValues = Yup.InferType<typeof dataInputValidationSchema>;

export interface CreateMap {
    id: string;
    name?: string;
    bgImage: File | null;
}

const newMapSchema = Yup.object({
    id: Yup.string().required(),
    name: Yup.string(),
    bgImage: Yup.mixed<File>()
        .required('Map background image is required')
        // .nonNullable()
        .test('fileSize', 'File size is too large (max 50 MB)', value => {
            return value && value.size <= 50 * 1024 * 1024;
        })
        .test('fileType', 'Unsupported file format', value => {
            return value && ['image/jpeg', 'image/png', 'application/pdf'].includes(value.type);
        }),
});

const existingMapSchema = Yup.object({
    id: Yup.string().required(),
    name: Yup.string(),
    bgImageUrl: Yup.string().required('Map image URL is required'),
});

const datasetSchema = Yup.object({
    id: Yup.string(),
    name: Yup.string().required('Dataset name is required'),
    order: Yup.number().required(),
});

export type DatasetSchema = Yup.InferType<typeof datasetSchema>;

const customMaterialSchema = Yup.object({
    id: Yup.string(),
    name: Yup.string().required().nonNullable(),
    categoryId: Yup.string().required(),
    order: Yup.number().required(),
    lbsPerYd3: Yup.number()
        .nullable()
        .transform(value => (isNaN(value) || value === null || value === undefined ? null : value)),
    mtCo2eSavedPerTon: Yup.number()
        .nullable()
        .transform(value => (isNaN(value) || value === null || value === undefined ? null : value)),
});

export type CustomMaterialSchema = Yup.InferType<typeof customMaterialSchema>;

export const eventFormSchema = Yup.object({
    eventName: Yup.string().required('Event name is required'),
    datasets: Yup.array(datasetSchema).required(),
    existingMaps: Yup.array(existingMapSchema).required(),
    customMaterials: Yup.array(customMaterialSchema).required(),
    newMaps: Yup.array(newMapSchema).required(),
    clientLogo: Yup.mixed<File>()
        .nullable()
        .optional()
        .test('fileSize', 'File size is too large', value => {
            return !value || value.size <= 5 * 1024 * 1024; // 5MB limit
        })
        .test('fileType', 'Unsupported file format', value => {
            return !value || ['image/jpeg', 'image/png'].includes(value.type);
        }),
    startDate: Yup.date().nullable(),
    endDate: Yup.date()
        .nullable()
        .test('is-after-start-date', 'End date must be after start date', function (endDate) {
            const { startDate } = this.parent;
            if (!startDate || !endDate) return true;
            return new Date(endDate) > new Date(startDate);
        }),
    clientName: Yup.string().nullable(),
    location: Yup.string().nullable(),
    welcomeDocId: Yup.string().nullable(),
    welcomeTabId: Yup.string().nullable(),
    welcomeDocUrlRaw: Yup.string().nullable(),
});

// TODO: extract this from eventFormSchema Yup.object?
export interface EventFormValues {
    eventName: string;
    existingMaps: EventMap[];
    datasets: DatasetSchema[];
    customMaterials: CustomMaterialSchema[];
    newMaps: CreateMap[];
    clientLogo: File | null;
    startDate: Date | null;
    endDate: Date | null;
    clientName: string | null;
    location: string | null;
    welcomeDocId: string | null;
    welcomeTabId: string | null;
    /** Raw URL input; parsed on submit so value is saved even if user didn't blur. */
    welcomeDocUrlRaw: string | null;
}

export const eventGroupFormSchema = Yup.object({
    groupName: Yup.string().required('Group name is required'),
    eventIds: Yup.array(Yup.string().required('Please select an event')).required(),
});

export interface EventForGroupValues {
    id: string | null;
    order: number;
}
// export type EventForGroupValues = Yup.InferType<typeof eventForGroupSchema>;

// export interface EventGroupFormValues {
//     groupName: string;
//     events: EventForGroup[];
// }
export type EventGroupFormValues = Yup.InferType<typeof eventGroupFormSchema>;

export type WasteMeasurementWithMaterial = InstaQLResult<
    AppSchema,
    {
        wasteMeasurements: {
            material: {
                category: {};
            };
        };
    }
>['wasteMeasurements'][number];

// Add validation schema for the creat report form
export const createReportFormSchema = Yup.object().shape({
    templateId: Yup.string().required('Template is required'),
    materialBreakdown: Yup.boolean().required(),
    materialIds: Yup.array().of(Yup.string()),
    categoryIds: Yup.array().of(Yup.string()),
    datasetIds: Yup.array().of(Yup.string()),
});

// User Permissions Query
export const queryAuthorizedUsersWithPermissions = () =>
    db.useQuery({
        $users: {},
        events: {
            ...sortByOrderField,
        },
    });

type QueryAuthorizedUsersResponse = NonNullable<ReturnType<typeof queryAuthorizedUsersWithPermissions>['data']>;
export type AuthorizedUserWithPermissions = QueryAuthorizedUsersResponse['$users'][number];
export type UserPermission = Pick<
    AuthorizedUserWithPermissions,
    | 'admin'
    | 'editMapStationsNotes'
    | 'createEditEventInfo'
    | 'viewWasteData'
    | 'editWasteData'
    | 'generateReports'
    | 'editTodos'
    | 'completeTodos'
    | 'viewInventory'
    | 'manageInventory'
    | 'editUsers'
    | 'allowedEventIds'
>;

// Define the form values interface
export interface CreateReportFormValues {
    templateId: string;
    materialBreakdown: boolean;
    materialIds: string[];
    categoryIds: string[];
    datasetIds: string[];
}

// Inventory entity types
export type PackingOperationStatus = 'active' | 'completed';
export type InventoryHistoryType = 'purchase' | 'lost' | 'broken' | 'consumed' | 'adjustment';

// Query functions for inventory entities
export const queryItemCategories = () =>
    db.useQuery({
        itemCategories: {
            $: { order: { order: 'asc' } },
        },
    });

export const queryItemTypes = () =>
    db.useQuery({
        itemTypes: {
            category: {},
            $: { order: { order: 'asc' } },
        },
    });

export const queryStorageLocations = (enabled: boolean = true) =>
    db.useQuery(
        enabled
            ? {
                  storageLocations: {
                      $: { fields: ['id', 'name', 'order'], order: { order: 'asc' } },
                  },
              }
            : null
    );

export const queryItemTypesWithLocations = (enabled: boolean = true) =>
    db.useQuery(
        enabled
            ? {
                  itemTypes: {
                      category: {},
                      itemLocations: {
                          event: {},
                          storageLocation: {},
                      },
                      $: { order: { order: 'asc' } },
                  },
              }
            : null
    );

/** Flat item locations for create-operation inventory maps — field-scoped. */
export const queryItemLocationsInventory = (enabled: boolean = true) =>
    db.useQuery(
        enabled
            ? {
                  itemLocations: {
                      $: { fields: ['quantity'] },
                      itemType: { $: { fields: ['id', 'name'] } },
                      event: { $: { fields: ['id'] } },
                      storageLocation: { $: { fields: ['id'] } },
                  },
              }
            : null
    );

/** All packing list items — flat query for dialog packing-list maps. */
export const queryPackingListItemsAll = (enabled: boolean = true) =>
    db.useQuery(
        enabled
            ? {
                  packingListItems: {
                      $: { fields: ['quantity'] },
                      event: { $: { fields: ['id'] } },
                      itemType: {
                          $: { fields: ['id', 'name', 'consumable'] },
                          category: { $: { fields: ['name'] } },
                      },
                  },
              }
            : null
    );

type QueryPackingListItemsAllResponse = NonNullable<ReturnType<typeof queryPackingListItemsAll>['data']>;
export type PackingListItemSlim = QueryPackingListItemsAllResponse['packingListItems'][number];

/** Minimal packing list item for dialog maps and task generation. */
export type PackingListItemMinimal = {
    quantity?: number | null;
    itemType?: {
        id?: string;
        name?: string;
        consumable?: boolean;
        category?: { name?: string | null } | null;
    } | null;
};

export const queryItemLocations = (eventId?: string | null, storageLocationId?: string | null) =>
    eventId
        ? db.useQuery({
              itemLocations: {
                  itemType: {
                      category: {},
                  },
                  event: {},
                  storageLocation: {},
                  $: {
                      where: {
                          'event.id': eventId,
                      },
                  },
              },
          })
        : storageLocationId
          ? db.useQuery({
                itemLocations: {
                    itemType: {
                        category: {},
                    },
                    event: {},
                    storageLocation: {},
                    $: {
                        where: {
                            'storageLocation.id': storageLocationId,
                        },
                    },
                },
            })
          : db.useQuery({
                itemLocations: {
                    itemType: {
                        category: {},
                    },
                    event: {},
                    storageLocation: {},
                },
            });

export const queryPackingListItems = (eventId: string) =>
    db.useQuery({
        events: {
            $: {
                where: {
                    id: eventId,
                },
            },
            packingListItems: {
                itemType: {
                    category: {},
                },
            },
        },
    });

export const queryWishListItems = (eventId: string) =>
    db.useQuery({
        events: {
            $: {
                where: {
                    id: eventId,
                },
            },
            wishListItems: {
                itemType: {
                    category: {},
                },
            },
        },
    });

/** Active operations for list display — includes task completion fields. */
export const queryActivePackingOperationsList = () =>
    db.useQuery({
        packingOperations: {
            $: { where: { status: 'active' }, order: { createdAt: 'desc' } },
            trip: { $: { fields: ['name'] } },
            sourceEvents: { $: { fields: ['name'] } },
            targetEvents: { $: { fields: ['name'] } },
            sourceStorageLocations: { $: { fields: ['name'] } },
            targetStorageLocations: { $: { fields: ['name'] } },
            packingTasks: { $: { fields: ['completed'] } },
        },
    });

/** Last 50 completed operations for list display — no packing tasks. */
export const queryCompletedPackingOperationsList = () =>
    db.useQuery({
        packingOperations: {
            $: {
                where: { status: 'completed' },
                order: { createdAt: 'desc' },
                limit: 50,
            },
            trip: { $: { fields: ['name'] } },
            sourceEvents: { $: { fields: ['name'] } },
            targetEvents: { $: { fields: ['name'] } },
            sourceStorageLocations: { $: { fields: ['name'] } },
            targetStorageLocations: { $: { fields: ['name'] } },
        },
    });

/** Active operations only — for trip/event locks (field-scoped). */
export const queryActivePackingOperations = (enabled: boolean = true) =>
    db.useQuery(
        enabled
            ? {
                  packingOperations: {
                      $: { where: { status: 'active' } },
                      trip: { $: { fields: ['id'] } },
                      sourceEvents: { $: { fields: ['id'] } },
                      targetEvents: { $: { fields: ['id'] } },
                  },
              }
            : null
    );

/** Existence check for create-operation guard — at most one row, id only. */
export const queryHasActivePackingOperation = (enabled: boolean = true) =>
    db.useQuery(
        enabled
            ? {
                  packingOperations: {
                      $: { where: { status: 'active' }, limit: 1, fields: ['id'] },
                  },
              }
            : null
    );

/** Full operation detail — scoped to a single operation. */
export const queryPackingOperationDetail = (operationId: string | null) =>
    db.useQuery(
        operationId
            ? {
                  packingOperations: {
                      $: { where: { id: operationId } },
                      trip: {},
                      sourceEvents: {},
                      targetEvents: {
                          packingListItems: {
                              itemType: {
                                  category: {},
                              },
                          },
                      },
                      sourceStorageLocations: {},
                      targetStorageLocations: {},
                      packingTasks: {
                          itemType: {
                              category: {},
                          },
                          fromEvent: {},
                          toEvent: {},
                          fromStorageLocation: {},
                          toStorageLocation: {},
                      },
                  },
              }
            : null
    );

/** @deprecated Use queryActivePackingOperationsList, queryCompletedPackingOperationsList, queryActivePackingOperations, or queryPackingOperationDetail. */
export const queryPackingOperations = queryActivePackingOperationsList;

export const queryInventoryHistory = (itemTypeId?: string) =>
    itemTypeId
        ? db.useQuery({
              inventoryHistory: {
                  itemType: {
                      category: {},
                  },
                  event: {},
                  storageLocation: {},
                  $: {
                      where: {
                          'itemType.id': itemTypeId,
                      },
                      order: { timestamp: 'desc' },
                  },
              },
          })
        : db.useQuery({
              inventoryHistory: {
                  itemType: {
                      category: {},
                  },
                  event: {},
                  storageLocation: {},
                  $: {
                      order: { timestamp: 'desc' },
                  },
              },
          });

// Type definitions from queries
type QueryItemCategoriesResponse = NonNullable<ReturnType<typeof queryItemCategories>['data']>;
export type ItemCategory = QueryItemCategoriesResponse['itemCategories'][number];

type QueryStorageLocationsResponse = NonNullable<ReturnType<typeof queryStorageLocations>['data']>;
export type StorageLocation = QueryStorageLocationsResponse['storageLocations'][number];

type QueryItemTypesResponse = NonNullable<ReturnType<typeof queryItemTypes>['data']>;
export type ItemType = QueryItemTypesResponse['itemTypes'][number];

export type QueryItemTypesWithLocationsResponse = NonNullable<ReturnType<typeof queryItemTypesWithLocations>['data']>;
export type ItemTypeWithLocations = QueryItemTypesWithLocationsResponse['itemTypes'][number];

type QueryItemLocationsResponse = NonNullable<ReturnType<typeof queryItemLocations>['data']>;
export type ItemLocation = QueryItemLocationsResponse['itemLocations'][number];
export type ItemLocationWithEvent = ItemLocation & {
    event: ItemLocation['event'];
    itemType: ItemLocation['itemType'];
};

type QueryPackingListItemsResponse = NonNullable<ReturnType<typeof queryPackingListItems>['data']>;
export type PackingListItem = NonNullable<QueryPackingListItemsResponse['events']>[number]['packingListItems'][number];

type QueryWishListItemsResponse = NonNullable<ReturnType<typeof queryWishListItems>['data']>;
export type WishListItem = NonNullable<QueryWishListItemsResponse['events']>[number]['wishListItems'][number];

export const queryTrips = () =>
    db.useQuery({
        trips: {
            events: {
                packingListItems: {
                    itemType: {
                        category: {},
                    },
                },
            },
            sourceStorageLocation: {},
            destStorageLocation: {},
            tripDagNodes: {},
            tripDagEdges: {},
            packingOperations: {
                packingTasks: {},
                $: { order: { createdAt: 'asc' } },
            },
            $: { order: { name: 'asc' } },
        },
        trailerCargoIncompatibilities: {
            trailerItemType: {},
            cargoItemType: {},
        },
    });

export const queryTripDetail = (tripId: string | null) =>
    db.useQuery(
        tripId
            ? {
                  trips: {
                      $: { where: { id: tripId } },
                      events: {
                          packingListItems: {
                              itemType: {
                                  category: {},
                              },
                          },
                      },
                      sourceStorageLocation: {},
                      destStorageLocation: {},
                      tripDagNodes: {},
                      tripDagEdges: {},
                      packingOperations: {
                          sourceEvents: {},
                          targetEvents: {},
                          sourceStorageLocations: {},
                          targetStorageLocations: {},
                          packingTasks: {},
                          $: { order: { createdAt: 'asc' } },
                      },
                  },
                  trailerCargoIncompatibilities: {
                      trailerItemType: {},
                      cargoItemType: {},
                  },
              }
            : null
    );

type QueryTripsResponse = NonNullable<ReturnType<typeof queryTrips>['data']>;
type TripsArray = QueryTripsResponse['trips'];
export type Trip = TripsArray extends readonly (infer E)[] ? E : TripsArray extends (infer E)[] ? E : never;

type QueryActivePackingOperationsListResponse = NonNullable<ReturnType<typeof queryActivePackingOperationsList>['data']>;
type QueryCompletedPackingOperationsListResponse = NonNullable<ReturnType<typeof queryCompletedPackingOperationsList>['data']>;
type ActivePackingOperationList = QueryActivePackingOperationsListResponse['packingOperations'][number];
type CompletedPackingOperationList = QueryCompletedPackingOperationsListResponse['packingOperations'][number];
export type PackingOperationList = ActivePackingOperationList | CompletedPackingOperationList;

type QueryPackingOperationDetailResponse = InstaQLResult<
    AppSchema,
    {
        packingOperations: {
            trip: {};
            sourceEvents: {};
            targetEvents: {
                packingListItems: {
                    itemType: {
                        category: {};
                    };
                };
            };
            sourceStorageLocations: {};
            targetStorageLocations: {};
            packingTasks: {
                itemType: {
                    category: {};
                };
                fromEvent: {};
                toEvent: {};
                fromStorageLocation: {};
                toStorageLocation: {};
            };
        };
    }
>;
export type PackingOperation = QueryPackingOperationDetailResponse['packingOperations'][number];
export type PackingTask = PackingOperation['packingTasks'][number];
export type PackingTaskWithDetails = PackingTask & {
    itemType: PackingTask['itemType'];
    fromEvent: PackingTask['fromEvent'];
    toEvent: PackingTask['toEvent'];
    fromStorageLocation: PackingTask['fromStorageLocation'];
    toStorageLocation: PackingTask['toStorageLocation'];
};

type QueryInventoryHistoryResponse = NonNullable<ReturnType<typeof queryInventoryHistory>['data']>;
export type InventoryHistory = QueryInventoryHistoryResponse['inventoryHistory'][number];
